import React, { useState } from 'react';
import { useAuth } from './SimpleAuthContext';
import { Card, CardHeader, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Check, CreditCard, Smartphone, Building as Bank } from 'lucide-react';
import axios from 'axios';

const Subscription = () => {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Single pricing plan - $45/quarter
  const pricingPlan = { price: 45, period: 'quarter', label: 'Quarterly', days: 90 };

  // Ensure API URL always ends with /api
  const getApiUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  };
  const API = getApiUrl();

  const handleSubscribe = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');

      // Get Razorpay order from backend
      const token = await currentUser.getIdToken();
      const response = await axios.post(
        `${API}/subscription/create-order`,
        { plan_type: 'quarterly' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { orderId, amount, currency, key, plan_label } = response.data;

      // Initialize Razorpay payment
      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: 'AI Powered BID and Project LOE/Productivity Estimator',
        description: `${plan_label} Subscription - $${pricingPlan.price}/${pricingPlan.period}`,
        order_id: orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await axios.post(
              `${API}/subscription/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (verifyResponse.data.success) {
              await refreshProfile();
              alert('Subscription activated successfully!');
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: userProfile?.name || '',
          email: currentUser.email || ''
        },
        theme: {
          color: '#9333ea'
        },
        method: {
          netbanking: true,
          card: true,
          upi: true,
          wallet: true,
          emi: true,
          paylater: true
        },
        config: {
          display: {
            blocks: {
              banks: {
                name: 'All payment methods',
                instruments: [
                  { method: 'card' },
                  { method: 'netbanking' },
                  { method: 'upi' },
                  { method: 'wallet' }
                ]
              }
            },
            sequence: ['block.banks'],
            preferences: {
              show_default_blocks: true
            }
          }
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal closed');
          }
        }
      };

      // Load Razorpay script and open payment modal
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          setError('Payment failed. Please try again.');
          console.error('Payment failed:', response.error);
        });
        rzp.open();
      };
      document.body.appendChild(script);

    } catch (err) {
      console.error('Subscription error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to initiate payment. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const daysLeft = userProfile?.trial_days_left || 0;
  const isTrialActive = userProfile?.subscription_status === 'trial' && daysLeft > 0;
  const isSubscribed = userProfile?.subscription_status === 'active';
  const trialExpired = userProfile?.subscription_status === 'expired';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Subscription Plan</h1>
          <p className="text-slate-600">Unlock unlimited access to all features</p>
        </div>

        {/* Trial Status Banner */}
        {isTrialActive && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-900 text-center">
              <strong>Free Trial Active:</strong> {daysLeft} days remaining
            </p>
          </div>
        )}

        {trialExpired && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-900 text-center">
              <strong>Trial Expired:</strong> Subscribe now to continue using all features
            </p>
          </div>
        )}

        {isSubscribed && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-900 text-center">
              <strong>Active Subscription</strong> - Thank you for being a premium member!
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-900 text-center">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free Trial Card */}
          <Card className={`relative ${isTrialActive ? 'border-blue-500 border-2' : ''}`}>
            <CardHeader>
              {isTrialActive && (
                <Badge className="absolute top-4 right-4 bg-blue-600">Current Plan</Badge>
              )}
              <h3 className="text-2xl font-bold">Free Trial</h3>
              <div className="text-3xl font-bold text-slate-900 mt-2">
                $0
                <span className="text-lg font-normal text-slate-600">/7 days</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm"><strong>3 project calculations</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm"><strong>Full calculator access</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm"><strong>Risk analysis & scenarios</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm"><strong>Resource optimization</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm"><strong>Commercial summary</strong></span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm"><strong>Save projects</strong></span>
                </li>
              </ul>
              <Button 
                disabled 
                variant="outline" 
                className="w-full"
              >
                {isTrialActive ? `${daysLeft} Days Left` : 'Trial Used'}
              </Button>
            </CardContent>
          </Card>

          {/* Premium Card - Single $45/quarter plan */}
          <Card className={`relative ${isSubscribed ? 'border-purple-500 border-2' : 'border-purple-300 border-2'}`}>
            <CardHeader>
              {isSubscribed ? (
                <Badge className="absolute top-4 right-4 bg-purple-600">Active</Badge>
              ) : (
                <Badge className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600">
                  Best Value
                </Badge>
              )}
              <h3 className="text-2xl font-bold">Premium</h3>
              
              {!isSubscribed && (
                <div className="text-3xl font-bold text-purple-600 mt-4">
                  ${pricingPlan.price}
                  <span className="text-lg font-normal text-slate-600">/{pricingPlan.period}</span>
                </div>
              )}
              
              {isSubscribed && (
                <div className="text-3xl font-bold text-purple-600 mt-2">
                  Active
                  <span className="text-lg font-normal text-slate-600"> Subscription</span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm font-semibold">Unlimited calculations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Full calculator access</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Risk analysis & scenarios</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Resource optimization tools</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Commercial summary</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">PDF exports & reports</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Analytics dashboard</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Team collaboration <span className="text-purple-600 font-semibold">(Coming Soon)</span></span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span className="text-sm">Priority support</span>
                </li>
              </ul>

              {!isSubscribed && (
                <>
                  <Button 
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 mb-4"
                  >
                    {loading ? 'Processing...' : `Subscribe - $${pricingPlan.price}/${pricingPlan.period}`}
                  </Button>

                  <div className="space-y-2">
                    <p className="text-xs text-slate-600 text-center font-semibold">Secure Payment Options:</p>
                    <div className="flex justify-center space-x-4 text-slate-500">
                      <div className="flex items-center space-x-1 text-xs">
                        <CreditCard className="h-4 w-4" />
                        <span>Cards</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs">
                        <Smartphone className="h-4 w-4" />
                        <span>UPI</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs">
                        <Bank className="h-4 w-4" />
                        <span>Net Banking</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isSubscribed && (
                <Button disabled className="w-full bg-green-600">
                  Active Subscription
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-xl font-bold mb-4">Frequently Asked Questions</h3>
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-semibold text-slate-900">Can I cancel anytime?</p>
              <p className="text-slate-600">Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">What payment methods do you accept?</p>
              <p className="text-slate-600">We accept Credit/Debit cards, UPI (GPay, PhonePe, Paytm), and Net Banking through Razorpay.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">Is my payment secure?</p>
              <p className="text-slate-600">Yes, all payments are processed securely through Razorpay with bank-level encryption.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">How does the quarterly billing work?</p>
              <p className="text-slate-600">You pay $45 every 3 months for unlimited access to all premium features. Your subscription renews automatically.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
